import { useEffect, useState, useMemo } from "react";
import { CalendarClock, Hourglass, Sparkles, PartyPopper, CheckCircle2, Loader2 } from "lucide-react";
import PaymentUpsellSection from "./PaymentUpsellSection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGES = [
  { id: "booked",      label: "Booked",      Icon: CalendarClock, blockKey: "pre" },
  { id: "soon",        label: "Soon",        Icon: Hourglass,     blockKey: "pre" },
  { id: "imminent",    label: "Almost time", Icon: Sparkles,      blockKey: "pre" },
  { id: "in_progress", label: "Happening",   Icon: PartyPopper,   blockKey: "during" },
  { id: "complete",    label: "Complete",    Icon: CheckCircle2,  blockKey: "post" },
];

const computeStage = (whenIso) => {
  if (!whenIso) return "booked";
  let t;
  try { t = new Date(whenIso).getTime(); } catch { return "booked"; }
  if (Number.isNaN(t)) return "booked";
  const now = Date.now();
  const diffH = (t - now) / (1000 * 60 * 60);
  if (diffH > 24)        return "booked";
  if (diffH > 1)         return "soon";
  if (diffH > -2)        return diffH > 0 ? "imminent" : "in_progress";
  return "complete";
};

export default function ConfirmationStageBanner({ rsvp, event }) {
  const [bundle, setBundle] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paymentsUpsells, setPaymentsUpsells] = useState({ payment_links: [], product_upsells: [], payment_note: "" });

  const appointmentAt = rsvp?.appointment_at || rsvp?.scheduled_at || event?.event_date || event?.start_at;
  const category = (event?.pilot_category || event?.category_id || "").toLowerCase();
  const stage = useMemo(() => computeStage(appointmentAt), [appointmentAt]);
  const stageIdx = STAGES.findIndex(s => s.id === stage);
  const current = STAGES[stageIdx] || STAGES[0];

  useEffect(() => {
    if (!category) return;
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const r = await fetch(`${API}/host/category-bundle?category_id=${encodeURIComponent(category)}`);
        if (r.ok && !cancel) {
          const d = await r.json();
          setBundle(d);
        }
      } catch {} finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [category]);
  
  // Fetch host's payment links + product upsells (Phase D)
  useEffect(() => {
    const evId = event?.event_id;
    if (!evId) return;
    let cancel = false;
    (async () => {
      try {
        const r = await fetch(`${API}/host/${evId}/payments-upsells`);
        if (r.ok && !cancel) setPaymentsUpsells(await r.json());
      } catch {}
    })();
    return () => { cancel = true; };
  }, [event?.event_id]);

  const blocks = (bundle?.confirmation_blocks?.[current.blockKey] || []);

  return (
    <div className="mb-5 rounded-2xl border border-gray-200 bg-gradient-to-br from-purple-50 via-white to-pink-50 p-4" data-testid="confirmation-stage-banner">
      {/* 5-stage progress pills */}
      <div className="flex items-center justify-between gap-1 mb-3" role="list" aria-label="Confirmation stages">
        {STAGES.map((s, i) => {
          const isCurrent = i === stageIdx;
          const isPast = i < stageIdx;
          const Icon = s.Icon;
          return (
            <div key={s.id} className="flex-1 flex flex-col items-center" role="listitem" data-testid={`stage-${s.id}`}>
              <div className={`w-7 h-7 rounded-full flex items-center justify-center border ${
                isCurrent ? "bg-purple-600 text-white border-purple-600 shadow"
                : isPast   ? "bg-purple-100 text-purple-600 border-purple-200"
                          : "bg-white text-gray-300 border-gray-200"
              }`}>
                <Icon className="w-3.5 h-3.5" />
              </div>
              <span className={`text-[9px] mt-1 font-medium ${isCurrent ? "text-purple-800" : "text-gray-400"}`}>
                {s.label}
              </span>
              {i < STAGES.length - 1 && (
                <div className={`h-px w-full -mt-3.5 ${isPast ? "bg-purple-200" : "bg-gray-100"}`} aria-hidden="true" style={{ position: "relative", top: "-14px", zIndex: -1 }} />
              )}
            </div>
          );
        })}
      </div>

      {/* Stage content - host's linked Client Experience blocks */}
      <div className="border-t border-gray-100 pt-3" data-testid={`stage-content-${current.id}`}>
        <div className="flex items-center gap-1.5 mb-1.5">
          <current.Icon className="w-4 h-4 text-purple-600" />
          <p className="text-sm font-semibold text-gray-900">
            {current.id === "booked"      && "You're booked"}
            {current.id === "soon"        && "Coming up soon"}
            {current.id === "imminent"    && "Almost time"}
            {current.id === "in_progress" && "Enjoy your experience"}
            {current.id === "complete"    && "How did it go?"}
          </p>
        </div>
        {loading ? (
          <Loader2 className="w-4 h-4 animate-spin text-purple-500" />
        ) : blocks.length === 0 ? (
          <p className="text-xs text-gray-500">
            {current.id === "booked"      && "Your provider will share prep notes here as your appointment gets closer."}
            {current.id === "soon"        && "Your appointment is coming up. Check back here for any prep your provider links."}
            {current.id === "imminent"    && "Get ready - we'll surface arrival details and what to bring here."}
            {current.id === "in_progress" && "We hope you're having a great time."}
            {current.id === "complete"    && "Share feedback or rebook from below."}
          </p>
        ) : (
          <ul className="space-y-2">
            {blocks.map((b, idx) => (
              <li key={b.id || idx} className="bg-white rounded-lg p-2.5 border border-gray-100" data-testid={`stage-block-${idx}`}>
                {b.title && <p className="text-xs font-semibold text-gray-800">{b.title}</p>}
                {b.description && <p className="text-[11px] text-gray-600 mt-0.5">{b.description}</p>}
                {b.url && (
                  <a href={b.url} target="_blank" rel="noopener noreferrer" className="text-[11px] text-purple-600 hover:underline mt-1 inline-block">
                    Open link
                  </a>
                )}
              </li>
            ))}
          </ul>
        )}

        {/* Iter 9.36 - Stage-specific dynamic CTAs */}
        <StageCTAs stage={current.id} rsvp={rsvp} event={event} appointmentAt={appointmentAt} />
      </div>

      {/* Phase D: Payment links + product upsells, stage-aware */}
      <PaymentUpsellSection
        paymentLinks={paymentsUpsells.payment_links || []}
        productUpsells={paymentsUpsells.product_upsells || []}
        paymentNote={paymentsUpsells.payment_note || ""}
        currentStage={current.id}
      />
    </div>
  );
}

// ─── Iter 9.36: Stage-specific CTAs ──────────────────────────────
function StageCTAs({ stage, rsvp, event, appointmentAt }) {
  const hasQuote = !!(rsvp?.quote_amount || rsvp?.quoted_amount);
  const isQuoted = rsvp?.status === "quoted" || (hasQuote && !rsvp?.payment_confirmed);
  const eventName = event?.name || "Appointment";
  const venue = event?.location || event?.venue || "";

  // Build .ics calendar download
  const buildIcsUrl = () => {
    if (!appointmentAt) return null;
    try {
      const dt = new Date(appointmentAt);
      const start = dt.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
      const endDt = new Date(dt.getTime() + 60 * 60 * 1000);
      const end = endDt.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
      const ics = [
        "BEGIN:VCALENDAR", "VERSION:2.0", "BEGIN:VEVENT",
        `UID:${rsvp?.rsvp_id || event?.event_id || "evt"}@phygital`,
        `DTSTART:${start}`, `DTEND:${end}`,
        `SUMMARY:${eventName}`,
        venue ? `LOCATION:${venue}` : "",
        "END:VEVENT", "END:VCALENDAR",
      ].filter(Boolean).join("\r\n");
      return "data:text/calendar;charset=utf-8," + encodeURIComponent(ics);
    } catch {
      return null;
    }
  };

  // Approve & Pay (any stage with a pending quote)
  const ApproveBtn = isQuoted && rsvp?.payment_link ? (
    <a
      href={rsvp.payment_link}
      target="_blank"
      rel="noopener noreferrer"
      className="block w-full text-center py-2.5 rounded-xl bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold"
      data-testid="stage-cta-approve-pay"
    >
      Approve &amp; Pay {rsvp?.quote_amount ? `$${rsvp.quote_amount}` : ""}
    </a>
  ) : null;

  const ReviewLink = `/c/${rsvp?.card_id || rsvp?.rsvp_id || event?.event_id}/review`;
  const icsUrl = buildIcsUrl();

  return (
    <div className="mt-3 space-y-2" data-testid={`stage-ctas-${stage}`}>
      {ApproveBtn}

      {stage === "booked" && icsUrl && (
        <a
          href={icsUrl}
          download={`${eventName.replace(/\s+/g, "_")}.ics`}
          className="block w-full text-center py-2 rounded-xl bg-white border border-purple-200 hover:bg-purple-50 text-purple-700 text-xs font-medium"
          data-testid="stage-cta-add-calendar"
        >
          Add to Calendar
        </a>
      )}

      {stage === "soon" && venue && (
        <a
          href={`https://maps.google.com/?q=${encodeURIComponent(venue)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="block w-full text-center py-2 rounded-xl bg-white border border-purple-200 hover:bg-purple-50 text-purple-700 text-xs font-medium"
          data-testid="stage-cta-directions"
        >
          Get Directions
        </a>
      )}

      {stage === "imminent" && (
        <div className="grid grid-cols-2 gap-2">
          {venue && (
            <a
              href={`https://maps.google.com/?q=${encodeURIComponent(venue)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="block text-center py-2 rounded-xl bg-white border border-purple-200 hover:bg-purple-50 text-purple-700 text-xs font-medium"
              data-testid="stage-cta-directions-imminent"
            >
              Directions
            </a>
          )}
          {event?.host_phone && (
            <a
              href={`tel:${event.host_phone}`}
              className="block text-center py-2 rounded-xl bg-white border border-purple-200 hover:bg-purple-50 text-purple-700 text-xs font-medium"
              data-testid="stage-cta-call-host"
            >
              Call Host
            </a>
          )}
        </div>
      )}

      {stage === "in_progress" && (
        <p className="text-[11px] text-center text-purple-700 italic">Tap notes from your provider as they post.</p>
      )}

      {stage === "complete" && (
        <a
          href={ReviewLink}
          className="block w-full text-center py-2.5 rounded-xl bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold"
          data-testid="stage-cta-review"
        >
          Leave a Review
        </a>
      )}
    </div>
  );
}
